import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Self,
  ViewEncapsulation
} from '@angular/core';
import { ControlContainer, NgControl } from '@angular/forms';
import { toBoolean } from '@ngx-simple/core/coercion';
import { MixinElementRefBase, mixinSize, ThemePalette } from '@ngx-simple/core/common-behaviors';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { InputBoolean } from '@ngx-simple/core/decorators';
import { SafeAny } from '@ngx-simple/core/types';
import { SimFormFieldComponent, SimFormFieldControl } from '@ngx-simple/simple-ui/form-field';
import { merge, Subject } from 'rxjs';
import {
  DateFilterFn,
  DateRangeInputParent,
  SimEndDateDirective,
  SimStartDateDirective,
  SIM_DATE_RANGE_INPUT_PARENT
} from './date-range-input.directive';
import { SimDateRangePickerComponent, SimDateRangePickerInput } from './date-range-picker.component';
import { DateRange, SimDateSelectionModel } from './date-selection-model';
import { SimDatepickerControl } from './datepicker-base';

let nextUniqueId = 0;

const _SimDateRangeInputMixinBase = mixinSize(MixinElementRefBase, 'medium');

@Component({
  selector: 'sim-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: SimFormFieldControl, useExisting: SimDateRangeInputComponent },
    { provide: SIM_DATE_RANGE_INPUT_PARENT, useExisting: SimDateRangeInputComponent }
  ],
  host: {
    role: 'group',
    class: 'sim-date-range-input',
    '[class.sim-date-range-input-required]': 'required',
    '[attr.data-sim-calendar]': 'rangePicker ? rangePicker.id : null'
  },
  exportAs: 'simDateRangeInput'
})
export class SimDateRangeInputComponent<D> extends _SimDateRangeInputMixinBase
  implements
    SimFormFieldControl<DateRange<D>>,
    SimDatepickerControl<D>,
    DateRangeInputParent<D>,
    SimDateRangePickerInput<D>,
    AfterContentInit,
    OnChanges,
    OnDestroy {
  /** 格式化input显示的时间字符串样式 */
  @Input() format: string;

  /**
   * 选择面板 决定 startView 值
   * - year 选择多年面板 => multi-year
   * - month 选择月份面板 => multi-year | year
   * - week 选择周面板 => multi-year | year | month
   * - date 选择日期面板 => multi-year | year | month
   * - datetime 选择日期时间面板 => multi-year | year | month | date
   */
  @Input() picker: 'year' | 'month' | 'week' | 'date' | 'datetime' = 'date';

  /** 最小有效日期 */
  @Input()
  get min(): D | null {
    return this._min;
  }
  set min(value: D | null) {
    this._min = this._getValidDateOrNull(value);
    this._revalidate();
  }

  /** 最大有效日期 */
  @Input()
  get max(): D | null {
    return this._max;
  }
  set max(value: D | null) {
    this._max = this._getValidDateOrNull(value);
    this._revalidate();
  }

  @Input()
  get disabled(): boolean {
    return this._startInput && this._endInput ? this._startInput.disabled && this._endInput.disabled : this._groupDisabled;
  }
  set disabled(value: boolean) {
    const newValue = toBoolean(value);

    if (newValue !== this._groupDisabled) {
      this._groupDisabled = newValue;
      this._disabledChange.next(this.disabled);
    }
  }

  /** 与sim-date-range-picker组件关联 */
  @Input()
  get rangePicker() {
    return this._rangePicker;
  }
  set rangePicker(rangePicker: SimDateRangePickerComponent<D>) {
    if (rangePicker) {
      this._model = rangePicker._registerInput(this);
      this._rangePicker = rangePicker;
      this._registerModel(this._model);
      this._rangePicker.closed.subscribe(() => {
        if (this._startInput) {
          this._startInput._onBlur();
        }
        if (this._endInput) {
          this._endInput._onBlur();
        }
      });
    }
  }

  /** 可用于过滤日期范围选择器中的日期的函数 */
  @Input()
  get dateFilter() {
    return this._dateFilter;
  }
  set dateFilter(value: DateFilterFn<D>) {
    this._dateFilter = value;
    this._revalidate();
  }

  get value() {
    return this._model ? this._model.selection : null;
  }

  get empty(): boolean {
    const startEmpty = this._startInput ? this._startInput.isEmpty() : false;
    const endEmpty = this._endInput ? this._endInput.isEmpty() : false;
    return startEmpty && endEmpty;
  }

  /** 分隔符 */
  @Input() separator: string = '~';

  /** 应该在日历中显示的比较范围的开始 */
  @Input() comparisonStart: D | null = null;

  /** 应该在日历中显示的比较范围的结束 */
  @Input() comparisonEnd: D | null = null;

  @Input()
  @InputBoolean<SimDateRangeInputComponent<D>, 'required'>()
  required: boolean;

  @ContentChild(SimStartDateDirective) _startInput: SimStartDateDirective<D>;
  @ContentChild(SimEndDateDirective) _endInput: SimEndDateDirective<D>;

  controlType = 'sim-date-range-input';

  /** 这里不需要提供 由携带`simStartDate`和`simEndDate`的input提供 */
  placeholder: string;

  focused = false;

  /** 输入是否处于错误状态 */
  get errorState(): boolean {
    if (this._startInput && this._endInput) {
      return this._startInput.errorState || this._endInput.errorState;
    }

    return false;
  }

  ngControl: NgControl | null;

  id = `sim-date-range-input-${nextUniqueId++}`;

  _stateChanges = new Subject<void>();

  focusedInput: SimStartDateDirective<D> | SimEndDateDirective<D>;

  readonly _disabledChange: Subject<boolean> = new Subject();
  readonly stateChanges: Subject<void> = new Subject();
  _groupDisabled: boolean;
  private _dateFilter: DateFilterFn<D>;
  private _rangePicker: SimDateRangePickerComponent<D>;
  private _min: D | null;
  private _max: D | null;

  /** 当前在输入中注册的日期选择模型 */
  private _model: SimDateSelectionModel<DateRange<D>> | undefined;

  constructor(
    _elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() control: ControlContainer,
    @Optional() private _dateAdapter: SimDateAdapter<D>,
    @Optional() private _formField?: SimFormFieldComponent
  ) {
    super(_elementRef);
    this.ngControl = control as SafeAny;
  }

  ngAfterContentInit(): void {
    if (!this._startInput) {
      throw Error('sim-date-range-input must contain a <input simStartDate />');
    }

    if (!this._endInput) {
      throw Error('sim-date-range-input must contain a <input simEndDate />');
    }

    if (this._model) {
      this._registerModel(this._model);
    }

    // 我们不需要取消订阅，因为我们知道输入流将在销毁时完成。
    merge(this._startInput.stateChanges, this._endInput.stateChanges).subscribe(() => {
      this.stateChanges.next(undefined);
    });
  }

  ngOnChanges() {
    this._stateChanges.next(undefined);
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._stateChanges.unsubscribe();
  }

  setDescribedByIds(ids: string[]): void {}

  onContainerClick(): void {
    if (!this.focused && !this.disabled) {
      if (!this._model || !this._model.selection.start) {
        this._startInput.focus();
      } else {
        this._endInput.focus();
      }
    }
  }

  /** 返回<input>的表单字段使用的调色板 */
  getThemePalette(): ThemePalette {
    return this._formField ? this._formField.color : undefined;
  }

  /** 获取日历应开始的日期 */
  getStartValue(): D | null {
    return this.value ? this.value.start : null;
  }

  /** 获取弹层相对元素 */
  getConnectedOverlayOrigin() {
    return this._elementRef;
  }

  /** 处理子输入更改中的值 */
  _handleChildValueChange() {
    this._changeDetectorRef.markForCheck();
  }

  _openDatePicker() {
    if (this._rangePicker) {
      this._rangePicker.open();
    }
  }

  _removeValue() {
    if (this._startInput) {
      this._startInput._removeValue();
    }

    if (this._endInput) {
      this._endInput._removeValue();
    }
  }

  private _getValidDateOrNull(obj: SafeAny): D | null {
    return this._dateAdapter.getValidDateOrNull(obj);
  }

  /** 每次start和end输入时候触发验证器 */
  private _revalidate() {
    if (this._startInput) {
      this._startInput._validatorOnChange();
    }

    if (this._endInput) {
      this._endInput._validatorOnChange();
    }
  }

  private _registerModel(model: SimDateSelectionModel<DateRange<D>>) {
    if (this._startInput) {
      this._startInput._registerModel(model);
    }

    if (this._endInput) {
      this._endInput._registerModel(model);
    }
  }
}
